// CTSK-347 — click-to-tool navigation resolver.
//
// Single source of truth for "where does clicking a tool land?",
// implementing precedence 1-2 of the CTSK-344 order (documented at
// the AppShell tile since CTSK-348):
//
//   1. tool.landingPath (manifest-declared, CTSK-347) — wins outright.
//      Relative routes ('/documents') navigate in-app via onNavigate;
//      absolute http(s) URLs open in a new tab (caller decides via
//      isExternalToolUrl — this module only resolves the string).
//   2. a known native type → the tool's native page
//      (NATIVE_TOOL_TYPES, 0.1.8).
//   3. fallback → Settings → Tools filtered by nest
//      (defaultToolsSettingsPath — the CTSK-346/348 URL shape).
//
// Callers pass a structural subset of the tool row: the sidebar tile
// consumes /stats/nests per-tool metadata ({id, slug, type,
// landingPath?}), while ToolsPanel rows read the stash off
// t.settings.landingPath (the API keeps the manifest field in the
// settings jsonb — no DB column; see MANIFEST_INTERNAL_SETTINGS_KEYS
// in apps/api/src/routes/tools.ts).

import { NATIVE_TOOL_TYPES } from './native-tool-types.js';

/** Structural subset of a tool row the resolver needs. */
export interface ToolLandingSource {
  type: string;
  /** Manifest-declared landing destination (CTSK-347). */
  landingPath?: string | null;
}

/** True when the resolved landing URL is an absolute http(s) URL —
 *  the caller should open it in a new tab (target="_blank" +
 *  rel="noopener noreferrer") instead of in-app onNavigate. */
export function isExternalToolUrl(url: string): boolean {
  return /^https?:\/\//.test(url);
}

/** The CTSK-346/348 default: Settings → Tools filtered by nest. */
export function defaultToolsSettingsPath(nestSlug: string): string {
  return `/settings?section=tools&nestSlug=${encodeURIComponent(nestSlug)}`;
}

/** CTSK-347 — read the landingPath stash off a tool row's settings
 *  jsonb (ToolsPanel rows carry settings verbatim). Tolerant of
 *  hand-edited rows: non-string / empty values read as unset. */
export function settingsLandingPath(settings: Record<string, unknown> | null | undefined): string | null {
  const raw = settings?.landingPath;
  return typeof raw === 'string' && raw.length > 0 ? raw : null;
}

/**
 * Resolve the landing URL for clicking a tool.
 *
 * Precedence: manifest landingPath → native page for known types →
 * Settings → Tools filtered by the tool's nest.
 */
export function resolveToolLandingUrl(
  tool: ToolLandingSource,
  nest: { slug: string },
): string {
  if (tool.landingPath) return tool.landingPath;
  const native = NATIVE_TOOL_TYPES[tool.type];
  if (native) return native;
  return defaultToolsSettingsPath(nest.slug);
}
